import { User } from '../types';
import { GOOGLE_CLIENT_ID, AUTHORIZED_DOMAINS as CONFIG_AUTHORIZED_DOMAINS, AUTHORIZED_EMAILS as CONFIG_AUTHORIZED_EMAILS } from './configService';

// Fallback values in case the environment variables are not available
const AUTHORIZED_DOMAINS = Array.isArray(CONFIG_AUTHORIZED_DOMAINS) && CONFIG_AUTHORIZED_DOMAINS.length > 0 
  ? CONFIG_AUTHORIZED_DOMAINS 
  : ['gmail.com']; // Default to gmail.com as a fallback

const AUTHORIZED_EMAILS = Array.isArray(CONFIG_AUTHORIZED_EMAILS) && CONFIG_AUTHORIZED_EMAILS.length > 0 
  ? CONFIG_AUTHORIZED_EMAILS 
  : ['herb0072@gmail.com']; // Default to the admin email as a fallback

// Debug log for authorization variables
console.log('[AUTH] Module loaded with authorization configuration:');
console.log('[AUTH] Imported GOOGLE_CLIENT_ID:', GOOGLE_CLIENT_ID);
console.log('[AUTH] Original CONFIG_AUTHORIZED_DOMAINS:', CONFIG_AUTHORIZED_DOMAINS);
console.log('[AUTH] Original CONFIG_AUTHORIZED_EMAILS:', CONFIG_AUTHORIZED_EMAILS);
console.log('[AUTH] Final AUTHORIZED_DOMAINS with fallback:', AUTHORIZED_DOMAINS);
console.log('[AUTH] Final AUTHORIZED_EMAILS with fallback:', AUTHORIZED_EMAILS);

// Interface for Google user profile
interface GoogleUserProfile {
  email: string;
  name: string;
  imageUrl?: string;
}

// Local storage key for user data
const USER_STORAGE_KEY = 'hb_user';

// Check if a user is authorized based on email
export const isAuthorizedUser = (email: string): boolean => {
  if (!email) {
    console.error('[AUTH] Cannot check authorization: email is empty');
    return false;
  }

  console.log(`[AUTH] Checking authorization for email: ${email}`);

  // Check if AUTHORIZED_EMAILS is properly defined
  if (!Array.isArray(AUTHORIZED_EMAILS)) {
    console.error('[AUTH] AUTHORIZED_EMAILS is not an array:', AUTHORIZED_EMAILS);
    return false;
  }

  // Check if AUTHORIZED_DOMAINS is properly defined
  if (!Array.isArray(AUTHORIZED_DOMAINS)) {
    console.error('[AUTH] AUTHORIZED_DOMAINS is not an array:', AUTHORIZED_DOMAINS);
    return false;
  }

  console.log(`[AUTH] AUTHORIZED_EMAILS (${AUTHORIZED_EMAILS.length}): ${AUTHORIZED_EMAILS.join(', ') || '(empty)'}`);
  console.log(`[AUTH] AUTHORIZED_DOMAINS (${AUTHORIZED_DOMAINS.length}): ${AUTHORIZED_DOMAINS.join(', ') || '(empty)'}`);

  // If both arrays are empty, log a warning but allow access (prevent lockout)
  if (AUTHORIZED_EMAILS.length === 0 && AUTHORIZED_DOMAINS.length === 0) {
    console.warn('[AUTH] Both AUTHORIZED_EMAILS and AUTHORIZED_DOMAINS are empty. Allowing access to prevent lockout.');
    return true;
  }

  // Check if the email is explicitly in the AUTHORIZED_EMAILS list
  if (AUTHORIZED_EMAILS.length > 0 && AUTHORIZED_EMAILS.includes(email)) {
    console.log(`[AUTH] User authorized by email match: ${email}`);
    return true;
  }

  // Extract domain from email
  const parts = email.split('@');
  if (parts.length !== 2) {
    console.error(`[AUTH] Invalid email format: ${email}`);
    return false;
  }

  const domain = parts[1];

  // Check if the email domain is in the AUTHORIZED_DOMAINS list
  if (domain && AUTHORIZED_DOMAINS.length > 0 && AUTHORIZED_DOMAINS.includes(domain)) {
    console.log(`[AUTH] User authorized by domain match: ${domain}`);
    return true;
  }

  console.log(`[AUTH] User not authorized: ${email}`);
  return false;
};

// Load the Google API script
export const loadGoogleAuthScript = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    // Check if the script is already loaded
    if (document.getElementById('google-auth-script')) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.id = 'google-auth-script';
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = (error) => reject(error);
    document.head.appendChild(script);
  });
};

// Initialize Google authentication
export const initGoogleAuth = async (): Promise<void> => {
  try {
    await loadGoogleAuthScript();

    // The Google Identity Services library should be loaded now
    // We'll handle the actual initialization in the Login component

    // Add a small delay to ensure the script is fully initialized
    await new Promise(resolve => setTimeout(resolve, 100));

    // Check if Google Identity Services is available
    if (!window.google || !window.google.accounts || !window.google.accounts.id) {
      console.warn('Google Identity Services not fully loaded after script load');
    }
  } catch (error) {
    console.error('Error initializing Google Auth:', error);
    throw error;
  }
};

// Process Google authentication response
export const handleGoogleAuthResponse = (response: any): User | null => {
  try {
    // Validate response and token
    if (!response || typeof response !== 'object') {
      console.error('Invalid Google auth response:', response);
      return null;
    }

    // Decode the JWT token from Google
    const token = response.credential;
    if (!token || typeof token !== 'string') {
      console.error('Invalid credential in Google auth response:', response);
      return null;
    }

    const tokenParts = token.split('.');
    if (tokenParts.length !== 3) {
      console.error('Invalid JWT token format:', token);
      return null;
    }

    const base64Url = tokenParts[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');

    let jsonPayload;
    try {
      jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
    } catch (e) {
      console.error('Error decoding JWT token:', e);
      return null;
    }

    let payload;
    try {
      payload = JSON.parse(jsonPayload);
    } catch (e) {
      console.error('Error parsing JWT payload:', e);
      return null;
    }

    const { email, name, picture } = payload;

    // Validate required fields
    if (!email || !name) {
      console.error('Missing required fields in JWT payload:', payload);
      return null;
    }

    // Check if the user is authorized
    if (!isAuthorizedUser(email)) {
      console.error('Unauthorized user attempted to login:', email);
      return null;
    }

    // Create user object
    const user: User = {
      email,
      name,
      imageUrl: picture,
      authProvider: 'google'
    };

    // Save user to local storage
    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));

    return user;
  } catch (error) {
    console.error('Error processing Google auth response:', error);
    return null;
  }
};

// Get the current user from local storage
export const getCurrentUser = (): User | null => {
  const userData = localStorage.getItem(USER_STORAGE_KEY);
  return userData ? JSON.parse(userData) : null;
};

// Logout the current user
export const logout = (): void => {
  localStorage.removeItem(USER_STORAGE_KEY);
};
